import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2 } from 'lucide-react';

const AdminProductManage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [formData, setFormData] = useState({
    name_tc: '',
    name_en: '',
    category_tc: '',
    category_en: '',
    description_tc: '',
    description_en: '',
    image_url: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
      return;
    }
    fetchProducts();
  }, [user, navigate]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products`);
      setProducts(response.data.products);
    } catch (error) {
      toast.error('無法載入產品');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentProduct) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/products/${currentProduct.product_id}`, formData);
        toast.success('產品更新成功');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/products`, formData);
        toast.success('產品建立成功');
      }
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error('操作失敗');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('確定要刪除嗎？')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/products/${productId}`);
      toast.success('刪除成功');
      fetchProducts();
    } catch (error) {
      toast.error('刪除失敗');
    }
  };

  const handleEdit = (product) => {
    setCurrentProduct(product);
    setFormData({
      name_tc: product.name_tc,
      name_en: product.name_en,
      category_tc: product.category_tc,
      category_en: product.category_en,
      description_tc: product.description_tc,
      description_en: product.description_en,
      image_url: product.image_url || ''
    });
    setIsEditing(true);
  };

  const resetForm = () => {
    setCurrentProduct(null);
    setFormData({
      name_tc: '',
      name_en: '',
      category_tc: '',
      category_en: '',
      description_tc: '',
      description_en: '',
      image_url: ''
    });
    setIsEditing(false);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="admin-product-manage">
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-2xl font-bold">產品管理</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {!isEditing ? (
          <div>
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold">產品列表</h2>
              <Button onClick={() => setIsEditing(true)} className="rounded-full" data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-2" />
                新增產品
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <div key={product.product_id} className="bg-white rounded-2xl overflow-hidden shadow-sm" data-testid={`product-item-${product.product_id}`}>
                  {product.image_url && (
                    <img src={product.image_url} alt={product.name_tc} className="w-full h-48 object-cover" />
                  )}
                  <div className="p-6">
                    <p className="text-sm text-primary font-semibold mb-2">{product.category_tc}</p>
                    <h3 className="text-xl font-bold mb-2">{product.name_tc}</h3>
                    <p className="text-slate-600 text-sm mb-4">{product.description_tc}</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(product)} data-testid={`edit-btn-${product.product_id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(product.product_id)} data-testid={`delete-btn-${product.product_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white p-8 rounded-3xl">
            <h2 className="text-3xl font-bold mb-8">{currentProduct ? '編輯產品' : '新增產品'}</h2>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="product-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">中文名稱</label>
                  <Input
                    value={formData.name_tc}
                    onChange={(e) => setFormData({...formData, name_tc: e.target.value})}
                    required
                    data-testid="name-tc-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">英文名稱</label>
                  <Input
                    value={formData.name_en}
                    onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                    required
                    data-testid="name-en-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">中文類別</label>
                  <Input
                    value={formData.category_tc}
                    onChange={(e) => setFormData({...formData, category_tc: e.target.value})}
                    required
                    data-testid="category-tc-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">英文類別</label>
                  <Input
                    value={formData.category_en}
                    onChange={(e) => setFormData({...formData, category_en: e.target.value})}
                    required
                    data-testid="category-en-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">圖片URL</label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="https://..."
                  data-testid="image-url-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">中文描述</label>
                  <Textarea
                    value={formData.description_tc}
                    onChange={(e) => setFormData({...formData, description_tc: e.target.value})}
                    rows={4}
                    required
                    data-testid="description-tc-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">英文描述</label>
                  <Textarea
                    value={formData.description_en}
                    onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                    rows={4}
                    required
                    data-testid="description-en-input"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <Button type="submit" className="rounded-full" data-testid="submit-btn">
                  {currentProduct ? '更新' : '建立'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm} className="rounded-full" data-testid="cancel-btn">
                  取消
                </Button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminProductManage;